import { FiRepeat } from 'react-icons/fi';

export const synonymsAntonymsData = {
  courseId: 'synonyms-antonyms',
  courseType: 'vocabulary',
  title: 'Synonyms & Antonyms',
  icon: FiRepeat,
  overview: {
    impact: 'Avoid repetition, understand paraphrasing in Reading, and boost your Writing Lexical Resource score',
    focus: 'Synonym degree scales, academic synonym pairs, antonym formation with prefixes, and paraphrasing strategies',
    uses: 'Reading (paraphrase recognition), Writing variety, Speaking lexical resource',
    duration: '20-25 minutes'
  },

  sections: [
    {
      title: 'Understanding Synonyms',
      content: `<p><strong>Synonyms</strong> are words with similar meanings. In IELTS, synonyms are critical because exam questions often <em>paraphrase</em> the original text — the answer uses a different word with the same meaning.</p>
<table>
  <thead><tr><th>Word</th><th>Common Synonyms</th><th>Context note</th></tr></thead>
  <tbody>
    <tr><td>important</td><td>significant, crucial, vital, essential</td><td>"Significant" is best for academic writing</td></tr>
    <tr><td>increase</td><td>rise, grow, expand, escalate</td><td>"Escalate" implies rapid/alarming increase</td></tr>
    <tr><td>problem</td><td>issue, challenge, difficulty, obstacle</td><td>"Challenge" has a more positive connotation</td></tr>
    <tr><td>show</td><td>demonstrate, illustrate, indicate, reveal</td><td>"Illustrate" works well with data/examples</td></tr>
    <tr><td>think</td><td>believe, consider, argue, maintain</td><td>"Argue" and "maintain" suit academic essays</td></tr>
  </tbody>
</table>`,
      points: [
        'Synonyms rarely have 100% identical meaning — there are differences in formality, connotation, and strength',
        'In IELTS Reading, the correct answer is almost always a paraphrase (synonym) of the text',
        'Using varied synonyms in Writing Task 2 directly improves your Lexical Resource band score'
      ]
    },
    {
      title: 'Synonym Degree Scales',
      content: `<p>Many synonyms can be arranged on a <strong>scale of intensity</strong>. Choosing the right degree makes your writing more precise and sophisticated.</p>
<table>
  <thead><tr><th>Scale</th><th>Mild → Moderate → Strong → Extreme</th></tr></thead>
  <tbody>
    <tr><td>Size</td><td>big → large → enormous → massive</td></tr>
    <tr><td>Good</td><td>nice → good → excellent → outstanding</td></tr>
    <tr><td>Bad</td><td>poor → bad → terrible → catastrophic</td></tr>
    <tr><td>Happy</td><td>pleased → happy → delighted → ecstatic</td></tr>
    <tr><td>Important</td><td>useful → important → crucial → indispensable</td></tr>
    <tr><td>Increase</td><td>rise → increase → surge → skyrocket</td></tr>
  </tbody>
</table>`,
      points: [
        'Using a word that is too strong or too weak for the context sounds unnatural',
        'In IELTS Writing Task 1, degree matters: a 2% rise is a "slight increase", not a "surge"',
        'Examiners reward precise vocabulary choice over complex vocabulary used inaccurately'
      ]
    },
    {
      title: 'Paraphrasing with Synonyms',
      content: `<p>Paraphrasing — saying the same thing in different words — is <strong>the most tested skill</strong> in IELTS Reading and a key requirement in Writing. Synonym substitution is the core technique.</p>
<table>
  <thead><tr><th>Original</th><th>Paraphrased</th><th>Technique</th></tr></thead>
  <tbody>
    <tr><td>Pollution has increased significantly.</td><td>Contamination has risen considerably.</td><td>Swap key words with synonyms</td></tr>
    <tr><td>Many people believe that…</td><td>It is widely held that…</td><td>Change structure + synonym</td></tr>
    <tr><td>The government should solve this problem.</td><td>The authorities ought to address this issue.</td><td>Multiple synonym swaps</td></tr>
    <tr><td>Children learn languages quickly.</td><td>Young learners acquire languages rapidly.</td><td>Replace nouns, verbs, and adverbs</td></tr>
  </tbody>
</table>`,
      points: [
        'Effective paraphrasing uses synonyms AND changes sentence structure',
        'In IELTS Writing Task 2, never copy the question word-for-word — paraphrase it in your introduction',
        'In Reading, if you find the same words as the question in the text, the answer is likely elsewhere (it will be paraphrased)'
      ]
    },
    {
      title: 'Antonyms and Opposites',
      content: `<p><strong>Antonyms</strong> are words with opposite meanings. They are useful for IELTS contrast expressions and can be formed in several ways.</p>
<table>
  <thead><tr><th>Method</th><th>Examples</th></tr></thead>
  <tbody>
    <tr><td>Prefix antonyms (un-, in-, dis-)</td><td>happy → unhappy, accurate → inaccurate, agree → disagree</td></tr>
    <tr><td>Completely different words</td><td>hot → cold, buy → sell, success → failure</td></tr>
    <tr><td>Degree antonyms (scale opposites)</td><td>tiny → massive, boiling → freezing, love → hate</td></tr>
  </tbody>
</table>
<p>In IELTS Writing, antonyms are useful for <strong>contrast</strong>: "While some people <em>support</em> this view, others <em>oppose</em> it."</p>`,
      points: [
        'Prefix antonyms are the easiest to learn — add un-, in-, dis-, or im- to reverse meaning',
        'Not all words form antonyms with prefixes: the opposite of "good" is "bad", not "ungood"',
        'IELTS Reading True/False/Not Given questions often use antonyms to create false statements'
      ]
    }
  ],

  exercises: [
    {
      title: 'Choose the Closest Synonym',
      type: 'select',
      instructions: 'Select the word closest in meaning to the word in bold.',
      questions: [
        {
          text: 'The project was deemed **crucial** for the community.',
          options: ['optional', 'essential', 'minor', 'simple'],
          answer: 'essential',
          explanation: '"Crucial" means extremely important or necessary, which is closest to "essential".'
        },
        {
          text: 'The research **demonstrated** a clear link between diet and health.',
          options: ['hid', 'denied', 'showed', 'questioned'],
          answer: 'showed',
          explanation: '"Demonstrated" means showed or proved clearly, making "showed" the closest synonym.'
        },
        {
          text: 'The new regulations will **diminish** the impact of pollution.',
          options: ['increase', 'reduce', 'maintain', 'ignore'],
          answer: 'reduce',
          explanation: '"Diminish" means to make smaller or less, which is synonymous with "reduce".'
        },
        {
          text: 'Many experts **advocate** for stronger environmental policies.',
          options: ['reject', 'support', 'postpone', 'review'],
          answer: 'support',
          explanation: '"Advocate" means to publicly recommend or support something.'
        }
      ]
    },
    {
      title: 'Match Antonym Pairs',
      type: 'select',
      instructions: 'Choose the correct antonym for each word.',
      questions: [
        {
          text: 'What is the antonym of "beneficial"?',
          options: ['helpful', 'harmful', 'neutral', 'ordinary'],
          answer: 'harmful',
          explanation: '"Beneficial" means having a good effect; its opposite is "harmful" — having a bad effect.'
        },
        {
          text: 'What is the antonym of "temporary"?',
          options: ['brief', 'permanent', 'recent', 'occasional'],
          answer: 'permanent',
          explanation: '"Temporary" means lasting for a limited time; "permanent" means lasting forever.'
        },
        {
          text: 'What is the antonym of "abundant"?',
          options: ['plentiful', 'scarce', 'moderate', 'excessive'],
          answer: 'scarce',
          explanation: '"Abundant" means existing in large quantities; "scarce" means in short supply.'
        },
        {
          text: 'What is the antonym of "decline"?',
          options: ['decrease', 'stabilise', 'increase', 'fluctuate'],
          answer: 'increase',
          explanation: '"Decline" means to go down or decrease; its antonym is "increase".'
        }
      ]
    },
    {
      title: 'Replace Words with Synonyms',
      type: 'fill-in',
      instructions: 'Rewrite the underlined word using an appropriate academic synonym.',
      questions: [
        {
          text: 'The government should _FIX_ this problem immediately. (Replace "fix" with an academic synonym)',
          answer: 'address',
          explanation: '"Address" is a more formal and academic alternative to "fix" when talking about problems.'
        },
        {
          text: 'Pollution levels have _GONE UP_ sharply this decade. (Replace "gone up")',
          answer: 'increased',
          explanation: '"Increased" is the standard academic synonym for "gone up".'
        },
        {
          text: 'Many people _THINK_ that education is the key to success. (Replace "think")',
          answer: 'believe',
          explanation: '"Believe" or "consider" are more suitable for academic writing than "think".'
        },
        {
          text: 'The study _LOOKED AT_ the effects of social media on teenagers. (Replace "looked at")',
          answer: 'examined',
          explanation: '"Examined" or "investigated" are academic alternatives to the informal "looked at".'
        }
      ]
    },
    {
      title: 'Choose the Correct Degree',
      type: 'select',
      instructions: 'Select the synonym that best fits the degree or intensity described in the sentence.',
      questions: [
        {
          text: 'There was a 50% jump in sales overnight — a truly _______ increase.',
          options: ['slight', 'moderate', 'dramatic', 'marginal'],
          answer: 'dramatic',
          explanation: 'A 50% overnight jump is a very large and sudden change, so "dramatic" fits the intensity.'
        },
        {
          text: 'Temperatures rose by just 0.5°C — a _______ change.',
          options: ['massive', 'negligible', 'significant', 'enormous'],
          answer: 'negligible',
          explanation: '0.5°C is very small, so "negligible" (so small as to be unimportant) is the right degree.'
        },
        {
          text: 'The charity received an _______ response, with thousands of volunteers signing up.',
          options: ['average', 'disappointing', 'overwhelming', 'mild'],
          answer: 'overwhelming',
          explanation: 'Thousands volunteering represents an extremely strong response, making "overwhelming" appropriate.'
        }
      ]
    },
    {
      title: 'Paraphrase Using Synonyms',
      type: 'writing',
      instructions: 'Rewrite each sentence using synonyms so that the meaning stays the same but no key content words are repeated.',
      questions: [
        {
          text: 'Paraphrase: "Many young people believe that technology has significantly improved their lives."',
          example: 'A large number of youths consider that technological advances have considerably enhanced their quality of life.',
          tip: 'Replace: many → a large number of, young people → youths, believe → consider, technology → technological advances, significantly → considerably, improved → enhanced, lives → quality of life.'
        },
        {
          text: 'Paraphrase: "Governments should invest more money in public transport to reduce traffic congestion."',
          example: 'Authorities ought to allocate greater funding to mass transit systems in order to alleviate traffic problems.',
          tip: 'Replace: governments → authorities, invest → allocate, money → funding, public transport → mass transit, reduce → alleviate, traffic congestion → traffic problems.'
        }
      ]
    }
  ],

  tips: [
    'Build a synonym notebook: for every new word, write 2-3 synonyms beside it',
    'Always check whether a synonym carries a different connotation or formality level before using it',
    'In IELTS Writing, avoid repeating the same word more than twice — use synonyms to show range',
    'For Reading True/False/Not Given, watch for antonyms that flip the meaning to create "False" answers'
  ],
  dailyPractice: [
    'Choose 5 common words each day and find 3 synonyms for each using a thesaurus',
    'Practise paraphrasing one paragraph from an English article daily',
    'Play the "synonym chain" game: start with one word and chain 5 synonyms in order of intensity',
    'Write pairs of contrasting sentences using antonyms: "While X increased, Y decreased"'
  ],
  backPath: '/exams/english/#vocabulary',
  backLabel: 'Back to Vocabulary',
  aiContext: 'Synonyms & Antonyms: Understanding synonym degree scales, academic synonym pairs, paraphrasing techniques, and antonym formation. Crucial for IELTS Reading (paraphrase recognition) and Writing (lexical variety). Difficulty 2 (pre-intermediate). Exercises include synonym identification, antonym matching, synonym replacement, degree selection, and paraphrasing practice.'
};

export function getDataForDifficulty(difficulty = 2) {
  if (difficulty <= 2) {
    return {
      ...synonymsAntonymsData,
      title: synonymsAntonymsData.title + ' (Foundations)',
      overview: { ...synonymsAntonymsData.overview, duration: '15-20 minutes' },
      sections: synonymsAntonymsData.sections.slice(0, Math.max(2, Math.ceil(synonymsAntonymsData.sections.length / 2))),
      exercises: synonymsAntonymsData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...synonymsAntonymsData,
      title: synonymsAntonymsData.title + ' (Advanced)',
      overview: { ...synonymsAntonymsData.overview, duration: '30-35 minutes' },
      sections: synonymsAntonymsData.sections,
      exercises: synonymsAntonymsData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(synonymsAntonymsData.tips || []),
        'For Band 8+: Use less common vocabulary naturally in context.',
        'Advanced tip: Focus on collocations and word families, not just individual words.',
      ],
    };
  }
  return synonymsAntonymsData;
}
